import React, { useEffect, useRef, useState } from 'react';
import TripLink from '../common/TripLink';
import { Plane, Clock, User, Mail, Lock, LockOpen, AlertTriangle, ChevronDown, ChevronUp, Radio } from 'lucide-react';
import { flightSide, flightProgress, withFlightProgress } from '../../utils/flightDisplay';
import { getAirlineByCode } from '../../utils/airlineUtils';
import { getComponentStartDatetime } from '../../utils/timelineFilters';
import FlightStatusBadge, { FlightDelayIndicator } from './FlightStatusBadge';
import { ClockTime } from './TimelineCardVisuals';
import { FlightFreshness, FlightInsights } from './FlightDataSummary';
import { PassengerNameList, formatPassengerName } from './PassengerNameList';
import SourceDocumentLinks from './SourceDocumentLinks';
import StickyNote from './StickyNote';
import FareTermsIcons, { resolveFareTerms } from './FareTermsIcons';

// Executive timeline (modern theme): items grouped by day, connecting flight
// legs of one booking merged into a single journey card, and an at-a-glance
// rail beside it.

const MONO = { fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace" };

// Instants only exist for timestamps carrying an offset; wall-clock values from
// different airports can't be subtracted.
const instantOf = (raw) => (/(Z|[+-]\d{2}:\d{2})$/.test(String(raw || '')) ? Date.parse(raw) : null);
const wallClockOf = (raw) => {
  const m = String(raw || '').match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  return m ? Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]) : null;
};

export const formatMinutes = (minutes) => {
  if (minutes == null || !Number.isFinite(minutes) || minutes <= 0) return null;
  const h = Math.floor(minutes / 60), m = Math.round(minutes % 60);
  return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
};

const minutesBetween = (fromRaw, toRaw, sameZone) => {
  const a = instantOf(fromRaw), b = instantOf(toRaw);
  if (a != null && b != null) return (b - a) / 60000;
  if (!sameZone) return null;
  const wa = wallClockOf(fromRaw), wb = wallClockOf(toRaw);
  return wa != null && wb != null ? (wb - wa) / 60000 : null;
};

const zoneAbbr = (timezone, raw) => {
  if (!timezone) return '';
  try {
    const when = instantOf(raw) ?? Date.now();
    const part = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' })
      .formatToParts(new Date(when)).find(p => p.type === 'timeZoneName');
    return part?.value || '';
  } catch { return ''; }
};

export const legCode = (leg, side, isPrivate) => (isPrivate
  ? (leg[`${side}_airport_icao`] || leg[`${side}_airport_iata`] || leg[`${side}_airport`] || '')
  : (leg[`${side}_airport`] || leg[`${side}_airport_code`] || ''));

export const legFlightNumber = (leg) => {
  let airline = leg.airline_code || leg.iata_code || '';
  const number = leg.flight_number || '';
  if (!airline) airline = (number.match(/^([A-Z0-9]{2})(?=\d)/i) || [])[1]?.toUpperCase() || '';
  const digits = airline && number.toUpperCase().startsWith(airline.toUpperCase()) ? number.slice(airline.length).trim() : number;
  return [airline, digits].filter(Boolean).join(' ');
};

const calendarDateOf = (component) => {
  if (component.isFlightLeg) {
    const dep = flightSide(component.flightLeg || {}, 'departure', {}, component.start_datetime);
    if (dep.calendarDate) return dep.calendarDate;
  }
  const raw = String(getComponentStartDatetime(component) || '');
  return (raw.match(/^(\d{4}-\d{2}-\d{2})/) || [])[1] || null;
};

export const formatDayLabel = (calendarDate, prefs = {}) => {
  if (!calendarDate) return 'Date to be confirmed';
  const opts = { timeZone: 'UTC', weekday: 'long', month: 'short', day: 'numeric' };
  return new Intl.DateTimeFormat(prefs.date_input_format === 'EU' ? 'en-GB' : 'en-US', opts)
    .format(new Date(`${calendarDate}T12:00:00Z`));
};

// A stop of a day or more ends the journey: an outbound and return booked
// together are two trips, not one with a multi-day layover.
const JOURNEY_BREAK_MINUTES = 24 * 60;

const continuesJourney = (prevLeg, leg) => {
  if (prevLeg.originalId !== leg.originalId) return false;
  const gap = minutesBetween(prevLeg.end_datetime || prevLeg.flightLeg?.arrival_datetime, leg.start_datetime || leg.flightLeg?.departure_datetime, true);
  return gap == null || gap < JOURNEY_BREAK_MINUTES;
};

/**
 * Groups sorted timeline items into days. Consecutive legs of the same flight
 * booking become one journey entry, filed under its first leg's day.
 */
export const groupTimelineByDay = (components) => {
  const days = [];
  let lastEntry = null;
  components.forEach((component) => {
    if (component.isFlightLeg && lastEntry?.kind === 'journey' && continuesJourney(lastEntry.legs[lastEntry.legs.length - 1], component)) {
      lastEntry.legs.push(component);
      return;
    }
    const date = calendarDateOf(component);
    let day = days[days.length - 1];
    if (!day || day.date !== date) {
      day = { date, entries: [], tripTitles: [] };
      days.push(day);
    }
    if (component.tripTitle && !day.tripTitles.includes(component.tripTitle)) day.tripTitles.push(component.tripTitle);
    lastEntry = component.isFlightLeg
      ? { kind: 'journey', key: `journey-${component.id}`, legs: [component] }
      : { kind: 'item', key: component.id, component };
    day.entries.push(lastEntry);
  });
  return days;
};

// One leg as a flight on a date between two airports, the same for every
// passenger booked on it.
const legSignature = (component) => {
  const leg = component.flightLeg || {};
  const isPrivate = component.isPrivateFlight || component.component_type === 'private_flight';
  const when = String(leg.departure_datetime || component.start_datetime || '');
  return [
    isPrivate ? (leg.tail_number || '') : legFlightNumber(leg).replace(/\s+/g, '').toUpperCase(),
    isPrivate ? when.slice(0, 16) : when.slice(0, 10),
    legCode(leg, 'departure', isPrivate),
    legCode(leg, 'arrival', isPrivate),
  ].join('|');
};

/**
 * Consolidated view: journeys of different bookings on the same series of
 * flights share one card. A booking matches a journey when it holds every one
 * of the journey's legs in a row, so a colleague on the same outbound but a
 * different return joins the outbound card only. `companionLegs` are other
 * travellers' flight legs on the same trips; matching journeys of the
 * executive's own bookings merge too. Each journey gains `companions`, one leg
 * list per extra booking.
 */
export const consolidateJourneys = (days, companionLegs = []) => {
  const bookings = new Map();
  companionLegs.forEach((leg) => {
    if (!bookings.has(leg.originalId)) bookings.set(leg.originalId, []);
    bookings.get(leg.originalId).push(leg);
  });
  const companionBookings = [...bookings.values()].map(legs => legs.slice().sort((a, b) => (a.legIndex ?? 0) - (b.legIndex ?? 0)));

  return days.map((day) => {
    const entries = [];
    const bySignature = new Map();
    day.entries.forEach((entry) => {
      if (entry.kind !== 'journey') { entries.push(entry); return; }
      const signature = entry.legs.map(legSignature);
      const key = `${entry.legs[0].tripId}::${signature.join('>')}`;
      const existing = bySignature.get(key);
      if (existing) { existing.companions.push(entry.legs); return; }
      const merged = { ...entry, companions: [] };
      companionBookings.forEach((legs) => {
        if (legs[0].tripId !== entry.legs[0].tripId) return;
        const sigs = legs.map(legSignature);
        for (let i = 0; i + signature.length <= sigs.length; i += 1) {
          if (signature.every((s, j) => sigs[i + j] === s)) {
            merged.companions.push(legs.slice(i, i + signature.length));
            return;
          }
        }
      });
      bySignature.set(key, merged);
      entries.push(merged);
    });
    return { ...day, entries };
  });
};

export const DayHeader =({ date, tripTitles, prefs }) => (
  <div className="flex items-baseline gap-3 pt-2 pb-2.5 border-b border-ui-divider">
    <h2 className="m-0 text-[15px] font-semibold text-ui-ink">{formatDayLabel(date, prefs)}</h2>
    {tripTitles.length > 0 && <span className="text-[13px] text-ui-muted truncate">{tripTitles.join(' · ')}</span>}
  </div>
);

const useAirlineNames = (codes) => {
  const [names, setNames] = useState({});
  const key = codes.join(',');
  useEffect(() => {
    let cancelled = false;
    key.split(',').filter(Boolean).forEach((code) => {
      getAirlineByCode(code).then((airline) => {
        if (!cancelled && airline?.name) setNames(prev => ({ ...prev, [code]: airline.name }));
      }).catch(() => {});
    });
    return () => { cancelled = true; };
  }, [key]);
  return names;
};

const Chip = ({ children, strong = false, title }) => (
  <span
    title={title}
    className={`inline-flex items-center h-[26px] px-2.5 rounded-md text-xs ${strong ? 'bg-ui-blue-bg text-ui-blue-fg font-semibold' : 'bg-ui-hover text-ui-ink-3'}`}
    style={strong ? MONO : undefined}
  >
    {children}
  </span>
);

// One airport stop on the journey rail: time column, dot + connector, details.
const StopRow = ({ time, scheduledTime, zone, updated, hollow, connector, title, subtitle, children, last }) => (
  <div className={`grid grid-cols-[76px_20px_minmax(0,1fr)] sm:grid-cols-[88px_24px_minmax(0,1fr)] gap-x-3 sm:gap-x-3.5 px-4 sm:px-[22px] ${last ? 'pb-5' : ''}`}>
    <div className="text-right">
      <div className={`text-base sm:text-lg font-semibold ${updated ? 'text-ui-green-fg' : 'text-ui-ink'}`} style={MONO}><ClockTime value={time} /></div>
      {scheduledTime && <div className="text-xs text-ui-muted line-through" style={MONO}><ClockTime value={scheduledTime} /></div>}
      {zone && <div className="text-xs text-ui-muted mt-0.5">{zone}</div>}
    </div>
    <div className="flex flex-col items-center">
      <span className={`w-3 h-3 mt-[5px] rounded-full flex-none ${hollow ? 'border-2 border-ui-blue-solid bg-ui-surface' : 'bg-ui-blue-solid'}`} />
      {connector === 'solid' && <span className="flex-1 w-0.5 bg-ui-blue-line mt-1" />}
      {connector === 'dashed' && <span className="flex-1 w-0 border-l-2 border-dashed border-ui-dash mt-1" />}
    </div>
    <div className={last ? '' : 'pb-3.5'}>
      <div className="text-[15px] font-semibold text-ui-ink break-words">{title}</div>
      {subtitle && <div className="text-[13px] text-ui-muted mt-0.5">{subtitle}</div>}
      {children}
    </div>
  </div>
);

const terminalGate = (leg, side, isPrivate) => {
  if (isPrivate) return leg[`${side}_fbo_name`] || leg[`${side}_fbo_address`] || '';
  const terminal = leg[`${side}_terminal`], gate = leg[`${side}_gate`];
  return `Terminal ${terminal || 'TBD'} · Gate ${gate || 'TBD'}`;
};

const effectiveStatus = (leg) => {
  const progress = flightProgress(leg);
  if (leg.diverted) return 'diverted';
  if (leg.flight_status) return leg.flight_status;
  return progress.completed ? null : 'scheduled';
};

const passengerNames = (legs) => {
  const names = [];
  const add = (raw) => {
    const name = formatPassengerName(raw);
    if (name && !names.includes(name)) names.push(name);
  };
  const cd = legs[0].component_data || legs[0].originalComponent?.component_data || {};
  (cd.passengers || []).forEach(p => add(typeof p === 'string' ? p : (p.name || p.passenger_name || `${p.first_name || ''} ${p.last_name || ''}`.trim())));
  add(cd.traveler_name || cd.passenger_name || cd.guest_name);
  if (names.length === 0) (legs[0].travellerNames || []).forEach(add);
  return names;
};

// Everyone on the card: the journey's own passengers, then each other booking's.
export const journeyPassengerNames = (legs, companions = []) => [legs, ...companions]
  .flatMap(passengerNames)
  .filter((name, i, all) => all.indexOf(name) === i);

/**
 * A flight booking as one card: every leg on a vertical rail, with the
 * layover between legs called out.
 */
export const JourneyCard = ({
  legs, prefs, airportName, stickyNotes = [], currentUserId, noteHandlers = {},
  onReportFeedback, onOpenConfidentiality, canViewTrip, canEditTrip, companions = [],
}) => {
  const cardRef = useRef(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const first = legs[0];
  const isPrivate = first.isPrivateFlight || first.component_type === 'private_flight';
  const rows = legs.map(component => {
    const leg = withFlightProgress(component.flightLeg || {});
    return {
      component, leg,
      dep: flightSide(leg, 'departure', prefs, component.start_datetime),
      arr: flightSide(leg, 'arrival', prefs, component.end_datetime),
      depCode: legCode(leg, 'departure', isPrivate),
      arrCode: legCode(leg, 'arrival', isPrivate),
    };
  });
  const lastRow = rows[rows.length - 1];
  const cd = first.component_data || first.originalComponent?.component_data || {};

  const airlineCodes = [...new Set(rows.map(r => (legFlightNumber(r.leg).split(' ')[0] || '')).filter(c => /^[A-Z0-9]{2,3}$/.test(c)))];
  const fetchedAirlines = useAirlineNames(isPrivate ? [] : airlineCodes);
  const airlines = [...new Set(rows.map(r => r.leg.airline_name || fetchedAirlines[legFlightNumber(r.leg).split(' ')[0]]).filter(Boolean))];

  const place = (code) => airportName(code) || code || 'TBD';
  const stops = rows.slice(0, -1).map(r => place(r.arrCode));
  const subtitle = [
    `${rows.length} ${rows.length === 1 ? 'flight' : 'flights'}`,
    stops.length === 0 ? (rows.length === 1 ? 'Nonstop' : null) : stops.length === 1 ? `1 stop in ${stops[0]}` : `${stops.length} stops`,
    isPrivate ? (first.flightLeg?.tail_number || cd.tail_number || 'Private') : airlines.join(', '),
  ].filter(Boolean).join(' · ');

  const totalMinutes = minutesBetween(rows[0].dep.raw, lastRow.arr.raw, false);
  const statusRow = rows.find(r => !flightProgress(r.leg).completed) || lastRow;
  const status = effectiveStatus(statusRow.leg);
  const tracking = rows.some(r => r.leg.has_active_alert);
  const delayed = rows.find(r => r.leg.delay_minutes != null);
  const passengers = journeyPassengerNames(legs, companions);
  const confirmation = cd.confirmation_number || first.confirmation_number;
  const seats = rows.map(r => r.leg.seat).filter(Boolean);
  const hasDetails = Boolean(confirmation || cd.ticket_number || seats.length);
  const flightId = first.originalComponent?.component_id || first.component_id;
  const componentId = first.originalId || first.id;
  const openConfidentiality = (e) => {
    e.stopPropagation();
    onOpenConfidentiality?.({ isUnfiled: false, tripId: first.tripId, componentId, title: first.title || 'Flight' });
  };

  return (
    <article
      ref={cardRef}
      className="relative bg-ui-surface border border-ui-line rounded-2xl overflow-hidden shadow-ui-card"
      data-component-id={first.id}
      data-trip-id={first.tripId}
    >
      {stickyNotes.map(note => (
        <div key={note.id} className="absolute z-20 pointer-events-auto" style={{ left: `${note.position?.offset_x || 5}%`, top: `${note.position?.offset_y || 5}%` }}>
          <StickyNote note={note} currentUserId={currentUserId} onToggleExpand={noteHandlers.onToggleNoteExpand} onEdit={noteHandlers.onEditNote} onDelete={noteHandlers.onDeleteNote} isDraggable isFloating containerRef={cardRef} currentComponentId={note.position?.attached_to_component_id} onPositionChange={noteHandlers.onNotePositionChange} onMoveToComponent={noteHandlers.onNoteMoveToComponent} />
        </div>
      ))}

      <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-[22px] py-[18px] border-b border-ui-divider">
        <div className="flex items-center gap-3.5 min-w-0">
          <div className={`w-10 h-10 rounded-[10px] flex items-center justify-center flex-none ${isPrivate ? 'bg-ui-violet-bg text-ui-violet-fg' : 'bg-ui-blue-bg text-ui-link'}`}>
            <Plane className="w-5 h-5" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h3 className="m-0 text-lg font-bold tracking-tight text-ui-ink">{place(rows[0].depCode)} → {place(lastRow.arrCode)}</h3>
            <div className="text-[13px] text-ui-muted mt-0.5">{subtitle}</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {tracking && (
            <span className="inline-flex items-center gap-1 h-[26px] px-2.5 rounded-full text-xs font-semibold bg-ui-green-bg text-ui-green-fg" title="Flight tracking active">
              <Radio className="w-3 h-3" aria-hidden="true" />Tracking
            </span>
          )}
          {status && <FlightStatusBadge status={status} delayMinutes={statusRow.leg.delay_minutes} size="small" variant="theme" />}
          {formatMinutes(totalMinutes) && <span className="text-[13px] font-semibold text-ui-ink-2">{formatMinutes(totalMinutes)} total</span>}
          <SourceDocumentLinks component={first} />
          <button
            type="button"
            onClick={openConfidentiality}
            aria-label={first.isConfidential ? 'Confidential: manage settings' : 'Manage confidentiality settings'}
            title="Manage confidentiality settings"
            className={`inline-flex items-center gap-1 h-9 px-2.5 rounded-lg text-xs font-medium ${first.isConfidential ? 'bg-ui-amber-bg text-ui-amber-fg' : 'text-ui-muted hover:text-ui-ink hover:bg-ui-hover'}`}
          >
            {first.isConfidential ? <><Lock className="w-3.5 h-3.5" aria-hidden="true" />Confidential</> : <LockOpen className="w-3.5 h-3.5" aria-hidden="true" />}
          </button>
        </div>
      </div>

      <div className="pt-5">
        {rows.map((row, idx) => {
          const next = rows[idx + 1];
          const isLast = !next;
          const flightNo = legFlightNumber(row.leg);
          const aircraft = row.leg.aircraft_type || (rows.length === 1 ? cd.aircraft_type : '');
          const legMinutes = minutesBetween(row.dep.raw, row.arr.raw, false);
          const layover = next ? minutesBetween(row.arr.raw, next.dep.raw, row.arrCode && row.arrCode === next.depCode) : null;
          const changesAirport = next && row.arrCode && next.depCode && row.arrCode !== next.depCode;
          const sameTerminal = next && !changesAirport && row.leg.arrival_terminal && row.leg.arrival_terminal === next.leg.departure_terminal;
          return (
            <React.Fragment key={row.component.id}>
              <StopRow
                time={row.dep.time}
                scheduledTime={row.dep.kind !== 'Scheduled' && row.dep.scheduled.time !== row.dep.time ? row.dep.scheduled.time : null}
                updated={row.dep.kind !== 'Scheduled'}
                zone={zoneAbbr(row.leg.departure_timezone, row.dep.raw)}
                hollow
                connector="solid"
                title={[row.depCode || 'TBD', airportName(row.depCode) || row.leg.departure_airport_name].filter(Boolean).join(' · ')}
                subtitle={terminalGate(row.leg, 'departure', isPrivate)}
              >
                <div className="flex flex-wrap gap-2 mt-3">
                  {(isPrivate ? row.leg.tail_number : flightNo) && <Chip strong>{isPrivate ? row.leg.tail_number : flightNo}</Chip>}
                  {aircraft && <Chip>{aircraft}</Chip>}
                  {formatMinutes(legMinutes) && <Chip>{formatMinutes(legMinutes)}</Chip>}
                  {row.leg.seat && <Chip>Seat {row.leg.seat}</Chip>}
                  <span className="ml-auto">
                    <FareTermsIcons terms={resolveFareTerms(row.leg,
                      row.component.component_data || row.component.originalComponent?.component_data)} />
                  </span>
                </div>
                <div className="mt-2 [&_.text-slate-500]:text-ui-muted [&_.text-amber-600]:text-ui-amber-fg [&_.text-red-600]:text-ui-red-fg">
                  <FlightInsights leg={row.leg} flightId={isPrivate ? null : flightId} prefs={prefs} />
                </div>
              </StopRow>
              <StopRow
                time={row.arr.time}
                scheduledTime={row.arr.kind !== 'Scheduled' && row.arr.scheduled.time !== row.arr.time ? row.arr.scheduled.time : null}
                updated={row.arr.kind !== 'Scheduled'}
                zone={zoneAbbr(row.leg.arrival_timezone, row.arr.raw)}
                connector={isLast ? null : 'dashed'}
                title={[row.arrCode || 'TBD', airportName(row.arrCode) || row.leg.arrival_airport_name].filter(Boolean).join(' · ')}
                subtitle={terminalGate(row.leg, 'arrival', isPrivate)}
                last={isLast}
              />
              {next && (
                <div className="grid grid-cols-[76px_20px_minmax(0,1fr)] sm:grid-cols-[88px_24px_minmax(0,1fr)] gap-x-3 sm:gap-x-3.5 px-4 sm:px-[22px]">
                  <div />
                  <div className="flex justify-center"><span className="w-0 border-l-2 border-dashed border-ui-dash" /></div>
                  <div className="pt-1 pb-4">
                    <div className="inline-flex flex-wrap items-center gap-2 px-3 py-2 rounded-[10px] bg-ui-amber-bg border border-ui-amber-line text-[13px] text-ui-amber-fg">
                      <Clock className="w-[15px] h-[15px]" aria-hidden="true" />
                      <strong className="font-semibold">{formatMinutes(layover) ? `${formatMinutes(layover)} connection` : 'Connection'}</strong>
                      {changesAirport && <span className="text-ui-amber-fg/80">· change airports to {next.depCode}</span>}
                      {sameTerminal && <span className="text-ui-amber-fg/80">· same terminal</span>}
                    </div>
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {companions.length > 0 && (
        <section aria-label="Passengers on these flights" className="px-4 sm:px-[22px] py-3 border-t border-ui-divider">
          <div className="text-xs text-ui-muted">{passengers.length} {passengers.length === 1 ? 'passenger' : 'passengers'} · {companions.length + 1} bookings</div>
          <ul className="m-0 mt-2 p-0 list-none flex flex-wrap gap-2">
            {passengers.map(name => (
              <li key={name} className="inline-flex items-center gap-1.5 h-[26px] px-2.5 rounded-md bg-ui-hover text-xs text-ui-ink-2">
                <User className="w-3 h-3 text-ui-muted" aria-hidden="true" />{name}
              </li>
            ))}
          </ul>
        </section>
      )}

      {delayed && (
        <div className="px-4 sm:px-[22px] pb-4">
          <FlightDelayIndicator delayMinutes={delayed.leg.delay_minutes} variant="theme" />
        </div>
      )}

      {hasDetails && detailsOpen && (
        <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 px-4 sm:px-[22px] py-3 border-t border-ui-divider text-xs">
          <dt className="text-ui-muted">Confirmation</dt>
          <dd className="m-0 font-medium text-ui-ink break-all">{confirmation || '—'}</dd>
          <dt className="text-ui-muted">Ticket</dt>
          <dd className="m-0 font-medium text-ui-ink break-all">{cd.ticket_number || '—'}</dd>
          <dt className="text-ui-muted">Seats</dt>
          <dd className="m-0 font-medium text-ui-ink break-all">{seats.length ? seats.join(', ') : '—'}</dd>
        </dl>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2.5 px-4 sm:px-[22px] py-3 bg-ui-sunk border-t border-ui-divider">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-ui-ink-3 min-w-0">
          {passengers.length > 0 && companions.length === 0 && <PassengerNameList names={passengers} variant="modern" IconComponent={User} />}
          {confirmation && <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" aria-hidden="true" />Confirmation on file</span>}
          <span className="[&_div]:text-ui-muted [&_.text-amber-600]:!text-ui-amber-fg">
            <FlightFreshness leg={statusRow.leg} prefs={prefs} />
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {onReportFeedback && (
            <button type="button" onClick={() => onReportFeedback(first)} aria-label="Report parsing issue" title="Report parsing issue" className="w-9 h-9 inline-flex items-center justify-center rounded-lg text-ui-muted hover:text-ui-amber-fg hover:bg-ui-hover">
              <AlertTriangle className="w-4 h-4" aria-hidden="true" />
            </button>
          )}
          {hasDetails && (
            <button type="button" onClick={(e) => { e.stopPropagation(); setDetailsOpen(v => !v); }} aria-expanded={detailsOpen} className="h-9 px-3 inline-flex items-center gap-1 rounded-lg border border-ui-control text-[13px] text-ui-ink-2 hover:bg-ui-hover">
              Booking details{detailsOpen ? <ChevronUp className="w-3.5 h-3.5" aria-hidden="true" /> : <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />}
            </button>
          )}
          {canViewTrip && (
            <TripLink to={`/Trip/${first.tripId}`} className="h-9 px-3 inline-flex items-center rounded-lg border border-ui-control text-[13px] text-ui-ink-2 hover:bg-ui-hover max-w-[180px] truncate" title={first.tripTitle}>
              {first.tripTitle || 'Trip'}
            </TripLink>
          )}
          {canEditTrip && (
            <TripLink to={`/Trip/${first.tripId}?editComponent=${componentId}`} className="h-9 px-3 inline-flex items-center rounded-lg border border-ui-control text-[13px] text-ui-ink-2 hover:bg-ui-hover">
              Edit
            </TripLink>
          )}
        </div>
      </div>
    </article>
  );
};

// ---------------------------------------------------------------------------
// At-a-glance rail
// ---------------------------------------------------------------------------

const RailCard = ({ title, action, children }) => (
  <section className="p-5 sm:px-[22px] rounded-2xl bg-ui-surface border border-ui-line">
    {(title || action) && (
      <div className="flex justify-between items-baseline gap-3">
        {title && <h3 className="m-0 text-[15px] font-semibold text-ui-ink">{title}</h3>}
        {action}
      </div>
    )}
    {children}
  </section>
);

const daysUntil = (calendarDate, now) => {
  if (!calendarDate) return null;
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((Date.parse(`${calendarDate}T00:00:00Z`) - today) / 86400000);
};

const countdownLabel = (days) => (days == null ? 'Date TBD' : days <= 0 ? 'Today' : days === 1 ? 'Tomorrow' : `${days} days`);

const joinWithAnd = (items) => (items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);

/**
 * Next departure, the journey it starts, and anything that needs a look.
 * `components` is the unfiltered timeline so the rail doesn't change with the
 * Today/Tomorrow/Upcoming toggle.
 */
export const AtAGlanceRail = ({ components, trips, prefs, airportName, canViewTrip }) => {
  const now = new Date();
  const upcomingLegs = components.filter(c => c.isFlightLeg && !flightProgress(withFlightProgress(c.flightLeg || {})).completed
    && (() => {
      const raw = flightSide(c.flightLeg || {}, 'departure', prefs, c.start_datetime).raw;
      const when = instantOf(raw) ?? (raw ? Date.parse(raw) : NaN);
      return !Number.isFinite(when) || when >= now.getTime() - 6 * 3600000;
    })());
  const nextLeg = upcomingLegs[0];
  const journeyLegs = nextLeg ? upcomingLegs.filter(c => c.originalId === nextLeg.originalId) : [];
  const nextTrip = nextLeg ? trips.find(t => t.id === nextLeg.tripId) : null;

  let hero = null;
  if (nextLeg) {
    const leg = nextLeg.flightLeg || {};
    const isPrivate = nextLeg.isPrivateFlight;
    const dep = flightSide(leg, 'departure', prefs, nextLeg.start_datetime);
    const depCode = legCode(leg, 'departure', isPrivate);
    const arrCode = legCode(leg, 'arrival', isPrivate);
    const shortDate = dep.calendarDate
      ? new Intl.DateTimeFormat(prefs.date_input_format === 'EU' ? 'en-GB' : 'en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(`${dep.calendarDate}T12:00:00Z`))
      : 'Date TBD';
    hero = { days: daysUntil(dep.calendarDate, now), shortDate, time: dep.time, depCode, arrCode, ident: isPrivate ? leg.tail_number : legFlightNumber(leg), terminal: leg.departure_terminal };
  }

  const journeyRows = journeyLegs.map(c => {
    const leg = c.flightLeg || {};
    return { dep: flightSide(leg, 'departure', prefs, c.start_datetime), arr: flightSide(leg, 'arrival', prefs, c.end_datetime) };
  });
  const doorToDoor = journeyRows.length ? formatMinutes(minutesBetween(journeyRows[0].dep.raw, journeyRows[journeyRows.length - 1].arr.raw, false)) : null;

  const attention = [];
  trips.filter(t => !t.start_date).forEach(t => attention.push({ key: `trip-${t.id}`, tone: 'warn', title: t.title || 'Untitled Trip', detail: 'Trip has no start date', to: canViewTrip(t.id) ? `/Trip/${t.id}` : null }));
  upcomingLegs.forEach(c => {
    const leg = withFlightProgress(c.flightLeg || {});
    const status = (leg.flight_status || '').toLowerCase();
    const ident = legFlightNumber(leg) || c.title;
    if (leg.cancelled || status.startsWith('cancel')) attention.push({ key: `cx-${c.id}`, tone: 'bad', title: `${ident} cancelled`, detail: c.tripTitle, to: canViewTrip(c.tripId) ? `/Trip/${c.tripId}` : null });
    else if (leg.diverted || status.includes('divert')) attention.push({ key: `dv-${c.id}`, tone: 'bad', title: `${ident} diverted`, detail: c.tripTitle, to: canViewTrip(c.tripId) ? `/Trip/${c.tripId}` : null });
    else if (leg.delay_minutes >= 15) attention.push({ key: `dl-${c.id}`, tone: 'warn', title: `${ident} delayed ${formatMinutes(leg.delay_minutes)}`, detail: c.tripTitle, to: canViewTrip(c.tripId) ? `/Trip/${c.tripId}` : null });
  });
  const gateless = journeyLegs.filter(c => !c.isPrivateFlight && !(c.flightLeg || {}).departure_gate).map(c => legFlightNumber(c.flightLeg || {})).filter(Boolean);
  if (gateless.length) {
    const tracked = journeyLegs.every(c => (c.flightLeg || {}).has_active_alert);
    attention.push({ key: 'gates', tone: 'info', title: gateless.length === 1 ? 'Gate not yet assigned' : 'Gates not yet assigned', detail: `${joinWithAnd(gateless)} · ${tracked ? "we'll notify you" : 'usually set closer to departure'}`, to: canViewTrip(nextLeg.tripId) ? `/Trip/${nextLeg.tripId}` : null });
  }
  const toneDot = { warn: 'bg-ui-amber-solid', bad: 'bg-ui-red-solid', info: 'bg-ui-blue-solid' };

  return (
    <aside aria-label="At a glance" className="flex flex-col gap-4 min-w-0">
      {hero && (
        <section className="p-[22px] rounded-2xl bg-ui-blue-bg border border-ui-blue-line">
          <div className="text-xs font-semibold tracking-[0.08em] uppercase text-ui-link">Next departure</div>
          <div className="text-4xl font-bold tracking-tight text-ui-ink mt-2.5">{countdownLabel(hero.days)}</div>
          <div className="text-sm text-ui-ink-2 mt-1">{[hero.shortDate, hero.time && `${hero.time} from ${hero.depCode || 'TBD'}`].filter(Boolean).join(' · ')}</div>
          <div className="flex flex-wrap items-center gap-2.5 mt-[18px] pt-4 border-t border-ui-blue-line">
            {hero.ident && <span className="text-[13px] font-semibold text-ui-blue-fg" style={MONO}>{hero.ident}</span>}
            <span className="text-[13px] text-ui-ink-2">{hero.depCode || 'TBD'} → {hero.arrCode || 'TBD'}{hero.terminal ? ` · Terminal ${hero.terminal}` : ''}</span>
          </div>
        </section>
      )}

      {nextTrip && (
        <RailCard
          title={nextTrip.title || 'Untitled Trip'}
          action={canViewTrip(nextTrip.id) ? <TripLink to={`/Trip/${nextTrip.id}`} className="text-[13px] text-ui-link hover:text-ui-blue-fg whitespace-nowrap">Open trip</TripLink> : null}
        >
          {nextTrip.start_date && <div className="text-[13px] text-ui-muted mt-0.5">Begins {formatDayLabel(String(nextTrip.start_date).slice(0, 10), prefs)}</div>}
          <div className="text-xs text-ui-muted mt-3">{airportName(hero?.depCode) || hero?.depCode} → {(() => { const l = journeyLegs[journeyLegs.length - 1]; const code = l ? legCode(l.flightLeg || {}, 'arrival', l.isPrivateFlight) : ''; return airportName(code) || code || 'TBD'; })()}</div>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {[
              [journeyLegs.length, journeyLegs.length === 1 ? 'Flight' : 'Flights'],
              [Math.max(journeyLegs.length - 1, 0), journeyLegs.length === 2 ? 'Stop' : 'Stops'],
              [doorToDoor || '—', 'Door to door'],
            ].map(([value, label]) => (
              <div key={label} className="p-3 rounded-[10px] bg-ui-raised min-w-0">
                <div className="text-lg sm:text-xl font-bold text-ui-ink truncate">{value}</div>
                <div className="text-xs text-ui-muted">{label}</div>
              </div>
            ))}
          </div>
        </RailCard>
      )}

      <RailCard title="Needs attention">
        {attention.length === 0 ? (
          <p className="text-[13px] text-ui-muted mt-2 mb-0">Nothing needs attention right now.</p>
        ) : (
          <ul className="list-none m-0 mt-3 p-0 flex flex-col gap-1">
            {attention.slice(0, 6).map(item => {
              const body = (
                <>
                  <span className={`flex-none w-2 h-2 rounded-full mt-1.5 ${toneDot[item.tone]}`} />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-ui-ink truncate">{item.title}</span>
                    {item.detail && <span className="block text-[13px] text-ui-muted">{item.detail}</span>}
                  </span>
                </>
              );
              return (
                <li key={item.key}>
                  {item.to
                    ? <TripLink to={item.to} className="flex gap-3 p-2.5 -mx-2.5 rounded-[10px] hover:bg-ui-hover">{body}</TripLink>
                    : <div className="flex gap-3 p-2.5 -mx-2.5">{body}</div>}
                </li>
              );
            })}
          </ul>
        )}
      </RailCard>
    </aside>
  );
};
